import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '@/api'
import { useAuth } from '@/context/AuthContext'
import { useToast } from '@/context/ToastContext'

/**
 * 音色试听的**唯一实现** —— 按需请求 → 播放 → 停止 → 失败如实说。
 *
 * ## 为什么是一个 hook，而不是继续留在按钮组件里
 *
 * 原先这套逻辑内联在 `VoicePreviewButton` 里。2026-10-08 加 `/tts` 的
 * 音色库（一次列 303 条）时，每一条都要有个「点一下听」的按钮 ——
 * 于是出现了两条路：
 *   · 把按钮塞进列表 303 次；
 *   · 或者**再抄一份**播放逻辑。
 * 前者的问题是 303 个独立播放器（能同时响好几个，而且都以为自己才是当前那个），
 * 后者违反「交互控件只许一份实现」。所以把逻辑提出来：
 *   · 按钮仍然是**一个实例一个播放器**（数字人页、单点试听）；
 *   · 音色库是**一个实例带 N 个触发点**（`toggle(id)`），天然保证「同一时刻只响一条」。
 *
 * ## 四条硬口径（都是踩过的）
 *
 * ① 🔴 **一次只许响一条**。列表里点了 B，A 必须立刻停 —— 不停的话，
 *    用户以为「B 的声音就是刚才那个」，而界面上 B 已经被高亮。这与本项目
 *    反复防的「界面说 A、实际给 B」是同一类病。实现上收在 `stop()` 里：
 *    任何一次 `toggle` 都先 `stop()`。
 *
 * ② 🔴 **在途请求必须能被作废**。试听是真花钱的合成（¥0.05/条），
 *    所以用户很可能在等待时改主意、点另一个音色。请求已经发出去了，钱照扣，
 *    但**声音绝不能事后冒出来** —— 那时候界面上高亮的是另一个音色。
 *    用请求令牌（`token`）判：`stop()` 步进令牌，晚到的响应看到令牌变了就丢弃。
 *
 * ③ **同一音色第二次点直接重播本地地址**，不打接口。后端也有缓存（命中不记账），
 *    但连请求都不发更干净 —— 一次会话里最多按「听过几个音色」计费。
 *    所以 `heard(id)` 是可以拿去给界面提示的（「已听过，再点不再计费」）。
 *
 * ④ **成功不弹提示，失败必须弹**，而且要把后端原话带出来：
 *    音色不认 / 上游挂了 / 没连上后端，三种原因的处置完全不同。
 *    ⚠️ `onerror` 与 `play()` 的 reject 是**同一件事的两个出口**，会先后都触发，
 *    所以同一次播放只放第一条过去（`reported`），否则叠两条 toast 说同一件事。
 *
 * ⑤ 首次合成（`billed`）之后**静默刷一次余额** —— 与「成功不弹提示」同口径，
 *    顶栏的数字自己会说明这次花了多少。判 `billed` 再刷：命中缓存那一次后端一分没扣。
 */
export type VoicePreviewState = {
  /** 当前「正在合成或正在播」的音色 ID；null = 静默。列表靠它决定哪一行显示暂停键 */
  active: string | null
  /** 正在向接口要音频（还没出声） */
  busy: boolean
  /** 正在出声 */
  playing: boolean
  /** 这个音色已经拿过地址了（再点只重播，不花钱也不打接口） */
  heard: (voiceId: string) => boolean
  /** 点一下：没在播就播，正在播就停；正在合成再点 = 放弃等待 */
  toggle: (voiceId: string) => void
  /** 立刻停下，并作废在途请求 */
  stop: () => void
}

export function useVoicePreview(sourcePage?: string): VoicePreviewState {
  const [active, setActive] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [playing, setPlaying] = useState(false)
  /**
   * 已经拿到的地址（音色 ID → 相对地址）。
   *
   * ⚠️ 用 state 而不是 ref：`heard()` 的结果要驱动界面（行上那句「已听过」提示），
   * ref 变了不会重渲染，界面会一直停在「还没听过」。
   * 条目数最多 = 本次会话听过的音色数（不缓存全部 303 条），够小。
   */
  const [urls, setUrls] = useState<Record<string, string>>({})
  /** async 回调里要读最新一份缓存，用 ref 镜像 */
  const urlsRef = useRef(urls)
  urlsRef.current = urls

  const audio = useRef<HTMLAudioElement | null>(null)
  /** 这一次播放已经报过错了（见头注 ④） */
  const reported = useRef(false)
  /** 请求令牌（见头注 ②） */
  const token = useRef(0)
  const alive = useRef(true)

  const toast = useToast()
  const { refresh: refreshBalance } = useAuth()

  const stop = useCallback(() => {
    token.current += 1 // 作废在途请求：晚到的响应不许出声
    const a = audio.current
    if (a) {
      a.pause()
      a.currentTime = 0
    }
    audio.current = null
    setActive(null)
    setPlaying(false)
    setBusy(false)
  }, [])

  /**
   * 页面切走时停掉，别让声音在别的页面上继续响。
   *
   * 🔴 开头那句 `alive.current = true` **不是多余的，删了整条链路就哑**。
   *
   * React 严格模式（dev）会把 effect 跑两遍：挂载 → 清理 → 再挂载。清理那一遍
   * 把 `alive` 置 false，而 `useRef(true)` 只在**首次渲染**初始化一次 ——
   * 第二次挂载**不会**把它重置回 true。少了这行，`alive` 就永久停在 false，
   * 于是下面 `.then` / `.catch` 里的 `if (!alive.current) return` 每次都提前返回：
   * 请求照发（HTTP 200）、钱照扣、**但 `setBusy(false)` 与 `start()` 永不执行**。
   *
   * 症状（2026-10-09 用户报的「点击无效哦」）：点了试听 → 圆圈一直转、
   * 没有声音、**也一条错误都不报**，因为所有正常出口都被那行 return 吞了。
   * 它不产生任何报错信息，是本项目最难查的那一类。
   *
   * ⚠️ 全站同一套写法在 `useVoices.ts` 里是**有**这行置回的（所以那份清单正常），
   * 本文件当时漏了。以后凡是「卸载后别 setState」的模式，都照这个形状写：
   * 置回 true 在 effect 体内，置 false 才在 cleanup 里。
   */
  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
      token.current += 1
      audio.current?.pause()
      audio.current = null
    }
  }, [])

  /** 统一起播。`tk` 是发起这次播放时的令牌，中途被 stop 过就不再出声 */
  const start = (url: string, voiceId: string, tk: number) => {
    const a = new Audio(url)
    audio.current = a
    reported.current = false
    a.onended = () => {
      if (tk !== token.current) return
      audio.current = null
      setPlaying(false)
      setActive(null)
    }
    // ⚠️ 播放失败**必须说出来**：静默失败的样子是「点了没声音」，
    // 用户会当成「这个音色就是这样」。
    a.onerror = () => fail('试听音频加载失败（文件可能已被清理）。再点一次会重新合成。', voiceId, tk)
    setActive(voiceId)
    void a
      .play()
      .then(() => {
        if (tk === token.current) setPlaying(true)
      })
      .catch(() => {
        // 慢 600ms 再报：文件本身有问题的话 `onerror` 会先到，
        // 那条更具体（「加载失败」比「浏览器拦下了播放」有用）。
        window.setTimeout(
          () => fail('浏览器拦下了播放，请再点一次试听。', voiceId, tk),
          600,
        )
      })
  }

  /** 统一报错出口：同一次播放只放第一条过去；并把该音色的缓存地址作废（它可能已被清理） */
  function fail(msg: string, voiceId: string, tk: number) {
    if (tk !== token.current || reported.current) return
    reported.current = true
    audio.current = null
    if (!alive.current) return
    setPlaying(false)
    setActive(null)
    setUrls((prev) => {
      const next = { ...prev }
      delete next[voiceId]
      return next
    })
    toast.error(msg)
  }

  const toggle = (voiceId: string) => {
    const id = (voiceId || '').trim()
    if (!id) return
    // 正在播 / 正在合成的是它自己 → 再点就是「停」
    if (active === id && (playing || busy)) {
      stop()
      return
    }
    stop()
    const tk = token.current

    // 这一条刚听过 → 直接重播，不再打接口（后端有缓存，但连请求都不发更干净）
    const cached = urlsRef.current[id]
    if (cached) {
      start(cached, id, tk)
      return
    }

    setActive(id)
    setBusy(true)
    void api
      .previewVoice({ voice: id, sourcePage })
      .then((res) => {
        if (tk !== token.current || !alive.current) return
        setUrls((prev) => ({ ...prev, [res.voice || id]: res.url }))
        // 只有真合成了才扣额度（命中缓存 `billed=false` 时后端一分没扣）。没扣就不刷。
        if (res.billed) void refreshBalance()
        setBusy(false)
        start(res.url, id, tk)
      })
      .catch((e: unknown) => {
        if (tk !== token.current || !alive.current) return
        setBusy(false)
        setActive(null)
        // 如实报错并带出后端原话：音色不认 / 上游合成挂了 / 后端没连上，
        // 三种原因处置完全不同，吞成「试听失败了」等于让人去猜。
        toast.error(`试听失败：${e instanceof Error ? e.message : String(e)}`)
      })
  }

  return {
    active,
    busy,
    playing,
    heard: (voiceId: string) => Boolean(urls[voiceId]),
    toggle,
    stop,
  }
}
